/**
 * The settings anchor hash: reading a target from the location hash, and
 * clearing it once a jump lands.
 *
 * Extracted from `settingsLayout.tsx` so that file stays under the
 * file-size ratchet; behavior is unchanged.
 *
 * @module
 */

import { useEffect, useState } from "react";

/** Frames to keep re-reading the hash after mount; covers the router writing it a tick later. */
const HASH_SETTLE_FRAMES = 10;

/**
 * The current URL hash, read from the document rather than the router.
 *
 * Upstream takes this from `useLocation`, which makes the page container throw
 * outside a `RouterProvider`. Upstream can afford that because nothing there
 * mounts the container in a test; six Forge suites render settings panels
 * standalone, and a scroll-to-anchor concern is not a good reason to make all
 * of them build a router. The hash is the same string either way.
 */
function readLocationHash(): string {
  // `window.location` is the thing being read, so it is the thing to guard:
  // the SSR suites run under a `window` that has no `location`, and a bare
  // `typeof window` check walks straight past that into a TypeError.
  return globalThis.window?.location?.hash ?? "";
}

/**
 * The location hash with the trailing settings target removed.
 *
 * Dropping the whole hash is right on web, where the hash IS the target, and
 * catastrophic on desktop, where hash history puts the entire route there:
 * clearing `#/settings/threads#threads-archive-confirmation` down to nothing
 * leaves the shell URL, so the page stays rendered from stale router state and
 * a refresh lands somewhere else.
 *
 * This is a bug the target-parsing fix *activated* rather than introduced. The
 * old parser never resolved a target on desktop, so `onTargetHandled` never
 * fired and this path never ran.
 */
export function hashWithoutTarget(hash: string): string {
  const cut = hash.lastIndexOf("#");
  // cut === 0 is the web shape (`#target`): nothing to keep. cut > 0 is the
  // desktop shape (`#/route#target`): keep everything up to the last `#`.
  return cut > 0 ? hash.slice(0, cut) : "";
}

/**
 * The settings target encoded in a location hash, for either router history.
 *
 * The desktop app builds its router with `createHashHistory` (see `main.tsx`),
 * so the whole route lives in the hash and a target arrives *nested*:
 * `#/settings/threads#threads-archive-confirmation`. On web it is the plain
 * `#threads-archive-confirmation`. Stripping only the leading `#` produced
 * `/settings/threads#threads-archive-confirmation` on desktop, which matches no
 * element, so every cross-page result silently landed at the top of the page --
 * invisible to browser QA, because the browser build never takes that path.
 *
 * Taking the segment after the LAST `#` is right for both. A value containing
 * `/` is a route rather than an id -- that is the hash-history case with no
 * target at all -- and must not be offered up as one.
 */
export function settingsTargetFromHash(hash: string): string | null {
  const target = hash.slice(hash.lastIndexOf("#") + 1);
  if (target.length === 0 || target.includes("/")) return null;
  return target;
}

export function useLocationHash(): string {
  const [hash, setHash] = useState(readLocationHash);
  useEffect(() => {
    const read = () => setHash(readLocationHash());
    read();

    // `history.replaceState` fires no event, and that is how the router writes
    // a redirect's hash -- so `hashchange` alone misses it whenever this mounts
    // in the same tick as the navigation that set it. Which of the two wins is
    // a race, and losing it looked exactly like arriving at a page with no
    // anchor. Re-read for a few frames instead of subscribing to the router,
    // which would make this hook throw in the suites that render settings
    // panels without one.
    let frames = 0;
    let raf = 0;
    const poll = () => {
      read();
      frames += 1;
      if (frames < HASH_SETTLE_FRAMES) raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);

    window.addEventListener("hashchange", read);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("hashchange", read);
    };
  }, []);
  return hash;
}
